import type { TFunction } from 'i18next';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatFileType } from './fileTypes';

/**
 * Human-readable disjunction of `accept` entries ("PDF or PNG", "Bild oder
 * PDF"), built from the same per-type labels the upload restriction hints use.
 *
 * Takes its formatter so callers that build the label for a list they only
 * learn at call time keep one formatter per locale instead of one per call.
 */
export function formatFileTypesLabel(
  types: readonly string[],
  t: TFunction<'ui'>,
  listFormatter: Intl.ListFormat,
): string {
  return listFormatter.format(types.map((type) => formatFileType(type, t)));
}

/** {@link formatFileTypesLabel} for the current locale. */
export function useFileTypesLabel(types: readonly string[]): string {
  const { i18n, t } = useTranslation('ui');
  const listFormatter = useMemo(
    () => new Intl.ListFormat(i18n.language, { type: 'disjunction' }),
    [i18n.language],
  );

  return formatFileTypesLabel(types, t, listFormatter);
}
